import { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { translations, SupportedLanguage } from '../i18n';
import type { TranslationKeys } from '../i18n/fr';
import { authService } from '../services/authService';
import { Logger } from '../utils/logger';

interface LanguageContextType {
  language: SupportedLanguage;
  /** User-initiated change: updates localStorage, syncs to backend (fire-and-forget). */
  setLanguage: (lang: SupportedLanguage) => void;
  /**
   * Hydration-only setter: updates state + localStorage WITHOUT triggering a backend PATCH.
   * Used by LanguageSync bridge to apply the language coming from the authenticated user object.
   */
  setLanguageLocal: (lang: SupportedLanguage) => void;
  t: TranslationKeys;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<SupportedLanguage>(() => {
    // Récupérer la langue depuis le localStorage ou utiliser le français par défaut
    const saved = localStorage.getItem('app-language');
    return (saved as SupportedLanguage) || 'fr';
  });

  useEffect(() => {
    // Sauvegarder la langue dans le localStorage
    localStorage.setItem('app-language', language);
  }, [language]);

  /** Hydration-only: called by LanguageSync when user object arrives from the backend. */
  const setLanguageLocal = useCallback((lang: SupportedLanguage) => {
    setLanguageState(lang);
    // localStorage is kept in sync via the useEffect above
  }, []);

  /** User-initiated change: persists to backend fire-and-forget when authenticated. */
  const setLanguage = useCallback((lang: SupportedLanguage) => {
    setLanguageState(lang);
    if (authService.isAuthenticated()) {
      authService.updateLanguage(lang).catch((err: unknown) => {
        Logger.warn('[LanguageContext] Failed to persist language preference to backend', {
          language: lang,
          error: err instanceof Error ? err.message : 'unknown_error',
        });
      });
    }
  }, []);

  const t = translations[language];

  const value = useMemo(
    () => ({ language, setLanguage, setLanguageLocal, t }),
    [language, setLanguage, setLanguageLocal, t]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
